/**
 * Camera viewshed-on-select (#262, founder ruling 2026-06-11) — the #178
 * terrain line-of-sight drape, now part of what a camera IS.
 *
 * Selecting ANY camera computes and drapes a TERRAIN-ACCURATE line-of-sight
 * footprint on the globe: the ground the camera can actually see, accounting for
 * terrain occlusion ("does this camera actually see the fire over that ridge?"). The
 * honest, terrain-real upgrade to the flat FOV cones, and the visible/defensible
 * backing for the cameraSmoke E.W.4 fusion (PRD §4.16): that fusion correlates a
 * camera to a hotspot within a 10 km smoke-LOS radius + ±30° bearing but does NOT
 * check terrain occlusion — this drape makes that correlation inspectable.
 *
 * NOT a standing layer any more: the old Fire-Risk "Camera Line-of-Sight
 * (modelled)" toggle is removed (PART IX — contextual render beats a standing
 * toggle). `setEnabled` now carries the per-operator viewshed-on-select pref
 * (default ON; the camera panel's "hide viewshed" affordance), and the whole
 * mechanic — World Terrain hold included — engages only while a camera is
 * actually selected:
 *   1. On select (enabled && camera): acquire the ref-counted World Terrain +
 *      depthTestAgainstTerrain (lib/terrainContext, #228 — coexists with the
 *      flood layer's hold), then compute ASYNC so the click stays snappy; the
 *      drape attaches when ready.
 *   2. Build a bounded RADIAL sample grid around the camera (azimuth sectors × range
 *      steps), limited to the FOV wedge for a directional camera or a full 360° sweep
 *      for a heading-unknown / PTZ camera, out to a bounded radius (the 10 km smoke-LOS
 *      radius for smoke_cv_eligible cameras — tying the fusion — else the camera range).
 *   3. Terrain-sample the whole grid in ONE sampleTerrainMostDetailed batch, then run
 *      the pure viewshedSample LOS test per ray (no Cesium dep — unit-tested).
 *   4. Drape the VISIBLE footprint as a translucent ground-clamped polygon
 *      (ClassificationType.TERRAIN — the PR #65 / PowerOutages ground-clamp pattern).
 *      Occluded ground is left UNpainted (Colour Law: no urgent-red field, no pulse).
 *   5. On deselect (or pref off): clear the footprint and RELEASE the terrain
 *      reference — the globe never stays upgraded with nothing selected.
 *
 * Imperative installer (MapView owns the viewer), mirroring the other layers:
 * setEnabled on the pref, setCamera on selection change, applyRegionGate on camera
 * moves, isReady for the control, destroy on unmount. Recompute AND the async
 * terrain acquire are guarded by one generation counter so a rapid select →
 * deselect can't leave a stale footprint attached or leak a terrain reference
 * (the flood pattern, extended to the acquire).
 */
import * as Cesium from 'cesium';
import type { CameraFeature } from '@tsr/shared-types';
import {
  MOUNT_HEIGHT_M,
  sweepAzimuths,
  buildRadialGrid,
  rayVisibility,
  destinationPoint,
  type RaySample,
} from '../viewshedSample';
import {
  VIEWSHED_VISIBLE_CSS,
  VIEWSHED_VISIBLE_ALPHA,
  VIEWSHED_OUTLINE_CSS,
  VIEWSHED_OUTLINE_ALPHA,
  VIEWSHED_OUTLINE_WIDTH,
} from '../styles/viewshed';
import { acquireWorldTerrain, releaseWorldTerrain } from '../lib/terrainContext';

/** Hard perf ceiling on the viewshed radius (m) regardless of the camera's range —
 *  bounds the terrain-sample count + keeps the footprint regional. */
export const VIEWSHED_MAX_RADIUS_M = 10_000;
/** The smoke line-of-sight radius the cameraSmoke fusion uses (PRD §4.16). A
 *  smoke_cv_eligible camera computes its viewshed to exactly this radius so the
 *  footprint answers "could this camera corroborate a hotspot here?". */
export const VIEWSHED_SMOKE_LOS_RADIUS_M = 10_000;
/** Angular resolution of the sweep (deg between rays). Tuned in Phase 0: 3° keeps the
 *  worst-case 360° / 10 km cold recompute (terrain-sample dominated) comfortably under
 *  the 2 s ceiling while still resolving ridge shadows; the reach polygon is smooth. */
export const VIEWSHED_AZ_STEP_DEG = 3;
/** Along-ground range-sample spacing (m). Tuned in Phase 0 (see AZ step note). */
export const VIEWSHED_RANGE_STEP_M = 100;
/** Above this camera height (m) the footprint is hidden so a country-zoom view never
 *  shows a sub-pixel speck (the footprint is ≤10 km across). */
const REGION_GATE_MAX_HEIGHT_M = 300_000;

export interface CameraViewshedLayerOptions {
  /** Test seam: inject the terrain sampler in place of Cesium's. Receives the same
   *  Cartographic[] the real call would, returns heights in array order. */
  sampler?: (cartographics: Cesium.Cartographic[]) => Promise<Cesium.Cartographic[]>;
  /** Test seam: skip the World Terrain swap (no ion token in unit tests). */
  skipTerrainSwap?: boolean;
  /** Test seam: provide the World Terrain provider in place of the ion-gated
   *  `createWorldTerrainAsync` — lets unit tests drive the REAL ref-counted
   *  acquire/release path (#262 select/deselect) with a fake provider. */
  terrainFactory?: () => Promise<Cesium.TerrainProvider>;
  /** Reports the chosen radius + ray/sample counts after a recompute (Phase 0 metrics). */
  onComputed?: (info: { radiusM: number; rays: number; samples: number; ms: number }) => void;
}

export interface CameraViewshedLayerHandle {
  /** The per-operator viewshed-on-select pref (#262). The mechanic — terrain
   *  hold + footprint — only engages while enabled AND a camera is selected. */
  setEnabled(enabled: boolean): void;
  /** Set (or clear) the selected camera. Select = acquire terrain + async
   *  compute; deselect = clear footprint + release the terrain reference. */
  setCamera(camera: CameraFeature | null): void;
  /** Show/hide the footprint based on camera proximity / zoom. */
  applyRegionGate(): void;
  /** True once a footprint has been computed for the current camera. */
  isReady(): boolean;
  /** Force a recompute for the current camera (e.g. after terrain resolves). */
  recompute(): void;
  destroy(): void;
}

const FOOTPRINT_ID = 'viewshed-footprint';
const OUTLINE_ID = 'viewshed-outline';

export function installCameraViewshedLayer(
  viewer: Cesium.Viewer,
  opts: CameraViewshedLayerOptions = {},
): CameraViewshedLayerHandle {
  const fillColor = Cesium.Color.fromCssColorString(VIEWSHED_VISIBLE_CSS).withAlpha(
    VIEWSHED_VISIBLE_ALPHA,
  );
  const outlineColor = Cesium.Color.fromCssColorString(VIEWSHED_OUTLINE_CSS).withAlpha(
    VIEWSHED_OUTLINE_ALPHA,
  );

  const ds = new Cesium.CustomDataSource('camera-viewshed');
  ds.show = false; // default OFF until enabled + a camera is selected
  void viewer.dataSources.add(ds);

  let enabled = false;
  let ready = false;
  let camera: CameraFeature | null = null;
  let gen = 0;

  // Whether THIS layer currently holds a World Terrain reference (the shared,
  // reference-counted terrainContext owns the actual capture/restore of the operator's
  // globe — so the viewshed and flood layers can both rely on World Terrain at once and
  // it survives until the LAST one switches off).
  let holdingTerrain = false;

  const swapInTerrain = async (myGen: number): Promise<void> => {
    if (opts.skipTerrainSwap || holdingTerrain || viewer.isDestroyed()) return;
    try {
      const terrain = await (opts.terrainFactory
        ? opts.terrainFactory()
        : Cesium.createWorldTerrainAsync());
      // A deselect/destroy while the provider loaded supersedes this acquire —
      // taking the reference anyway would pin the globe with no future release.
      if (myGen !== gen || viewer.isDestroyed() || holdingTerrain) return;
      acquireWorldTerrain(viewer, terrain);
      holdingTerrain = true;
    } catch (err) {
      // Without an ion token this throws — degrade to the existing terrain. Sampling
      // will then return flat heights and the footprint reads as an open-sightline
      // disc (no occlusion), which is honest given no terrain is available.
      console.error('camera-viewshed: World Terrain unavailable (ion token?)', err);
    }
  };

  const restoreTerrain = (): void => {
    if (!holdingTerrain) return;
    holdingTerrain = false;
    releaseWorldTerrain(viewer);
  };

  const clearFootprint = (): void => {
    ds.entities.removeAll();
    ready = false;
  };

  /** Choose the viewshed radius for a camera: the 10 km smoke-LOS radius for
   *  smoke_cv_eligible cameras (ties the fusion), else the camera's display range,
   *  capped at the perf ceiling. */
  const radiusFor = (cam: CameraFeature): number => {
    if (cam.properties.smoke_cv_eligible) return VIEWSHED_SMOKE_LOS_RADIUS_M;
    const r = cam.properties.range_m;
    return Math.max(200, Math.min(VIEWSHED_MAX_RADIUS_M, Number.isFinite(r) ? r : 1500));
  };

  /** Build the azimuth sweep for a camera: the FOV wedge for a directional fixed cam,
   *  a full 360° sweep for a heading-unknown or PTZ (rotating) camera. */
  const azimuthsFor = (cam: CameraFeature): number[] => {
    const ptzOrUnknown = cam.properties.is_ptz || !cam.properties.heading_known;
    return sweepAzimuths({
      headingDeg: ptzOrUnknown ? null : cam.properties.heading_deg,
      fovDeg: cam.properties.fov_deg,
      stepDeg: VIEWSHED_AZ_STEP_DEG,
    });
  };

  const sample = (cartographics: Cesium.Cartographic[]): Promise<Cesium.Cartographic[]> => {
    if (opts.sampler) return opts.sampler(cartographics);
    return Cesium.sampleTerrainMostDetailed(viewer.terrainProvider, cartographics);
  };

  const recompute = async (myGen: number): Promise<void> => {
    if (!enabled || !camera) {
      clearFootprint();
      return;
    }
    const cam = camera;
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    const [lon, lat] = cam.geometry.coordinates;
    const origin = { lon, lat };
    const radiusM = radiusFor(cam);
    const directional = !(cam.properties.is_ptz || !cam.properties.heading_known);
    const azimuths = azimuthsFor(cam);
    const grid: RaySample[][] = buildRadialGrid(
      origin,
      azimuths,
      VIEWSHED_RANGE_STEP_M,
      radiusM,
    );

    // Flatten to one terrain-sample batch: index 0 = origin, then every ray's samples
    // in order so we can slice the results back per ray.
    const cartos: Cesium.Cartographic[] = [Cesium.Cartographic.fromDegrees(lon, lat)];
    for (const ray of grid) {
      for (const s of ray) cartos.push(Cesium.Cartographic.fromDegrees(s.lon, s.lat));
    }
    const sampleCount = cartos.length;

    let sampled: Cesium.Cartographic[];
    try {
      sampled = await sample(cartos);
    } catch (err) {
      console.error('camera-viewshed: terrain sample failed', err);
      return;
    }
    if (myGen !== gen) return; // a newer camera/toggle superseded this run

    const originHeight =
      sampled[0] && Number.isFinite(sampled[0].height) ? sampled[0].height : 0;
    const eyeHeightM = originHeight + MOUNT_HEIGHT_M;

    // Per-ray LOS → the farthest contiguous-visible ground point (the reach). Connect
    // those reach points into a star-shaped footprint ring around the camera.
    const reachPoints: { lon: number; lat: number }[] = [];
    let cursor = 1; // index into `sampled` (0 was the origin)
    let anyReach = false;
    for (let r = 0; r < grid.length; r++) {
      const ray = grid[r]!;
      const samples = ray.map((s, i) => ({
        distM: s.distM,
        terrainHeightM:
          sampled[cursor + i] && Number.isFinite(sampled[cursor + i]!.height)
            ? sampled[cursor + i]!.height
            : originHeight,
      }));
      cursor += ray.length;
      const { reachM } = rayVisibility(eyeHeightM, samples);
      if (reachM > 0) anyReach = true;
      // Clamp the reach to a minimum so a fully-occluded azimuth still pulls the ring
      // to the camera (an indentation = the occlusion shadow) without degenerate 0s.
      const d = Math.max(0, reachM);
      const p = d > 0 ? destinationPoint(origin, azimuths[r]!, d) : origin;
      reachPoints.push(p);
    }

    clearFootprint();
    if (anyReach && reachPoints.length >= 3) {
      // For a directional wedge, close the ring back through the camera apex so it
      // reads as a fan; a 360° sweep is already a closed loop around the camera.
      const ring = directional ? [origin, ...reachPoints] : reachPoints;
      const flat: number[] = [];
      for (const p of ring) flat.push(p.lon, p.lat);
      const positions = Cesium.Cartesian3.fromDegreesArray(flat);

      ds.entities.add({
        id: FOOTPRINT_ID,
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(positions),
          material: fillColor,
          classificationType: Cesium.ClassificationType.TERRAIN,
        },
      });
      // Crisp ground-clamped edge so the visible/occluded boundary reads (the ridge
      // shadow line). A separate clamped polyline — polygon outlines don't classify.
      ds.entities.add({
        id: OUTLINE_ID,
        polyline: {
          positions: [...positions, positions[0]!],
          clampToGround: true,
          width: VIEWSHED_OUTLINE_WIDTH,
          material: outlineColor,
        },
      });
    }

    ready = true;
    applyRegionGate();
    const ms = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
    opts.onComputed?.({ radiusM, rays: grid.length, samples: sampleCount, ms });
  };

  const applyRegionGate = (): void => {
    if (!enabled || !camera || viewer.isDestroyed()) {
      ds.show = false;
      return;
    }
    const height = viewer.camera.positionCartographic?.height ?? 0;
    ds.show = height < REGION_GATE_MAX_HEIGHT_M;
  };

  // #262 lifecycle — one sync point for both inputs (pref + selection). The
  // whole mechanic engages only while enabled AND a camera is selected: that's
  // when the terrain hold is acquired and the async compute kicks off (the
  // click stays snappy — the drape attaches when ready). Deselecting (or
  // turning the pref off) clears the footprint and releases the terrain
  // reference, so the globe never stays upgraded with nothing selected.
  const sync = (): void => {
    gen++; // invalidate any in-flight acquire/recompute
    const myGen = gen;
    if (enabled && camera) {
      void (async () => {
        await swapInTerrain(myGen);
        if (myGen !== gen) return;
        void recompute(myGen);
      })();
    } else {
      clearFootprint();
      ds.show = false;
      restoreTerrain();
    }
  };

  return {
    setEnabled(next: boolean): void {
      if (next === enabled) return;
      enabled = next;
      sync();
    },
    setCamera(next: CameraFeature | null): void {
      if (next === camera) return;
      camera = next;
      sync();
    },
    applyRegionGate,
    isReady(): boolean {
      return ready;
    },
    recompute(): void {
      if (!enabled || !camera) return;
      gen++;
      void recompute(gen);
    },
    destroy(): void {
      gen++;
      restoreTerrain();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
